import { useCallback, useEffect, useRef, useState } from 'react';

import { useShop } from '~features/paywall';
import { useIsCustomer } from '~features/tags';
import { Toaster } from '~features/toaster';
import { useBool } from '~hooks/useBool';
import { useUserId } from '~hooks/useUserId';
import { GTMAnalytics } from '~services/analytics';
import { useOneClickBuyMutation } from '~services/api/credits';
import type {
	TShopItem,
	TShopPromoCodeResponse,
} from '~services/api/credits/types';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';

export const usePurchase = ({ onPurchased }: { onPurchased: () => void }) => {
	const userId = useUserId();
	const { oneClickPaymentAvailable, country } = useShop().data ?? {};

	const [oneClickBuy] = useOneClickBuyMutation();

	const [isPaymentFormOpened, openPaymentForm, closePaymentForm] = useBool();
	const [purchasing, setPurchasing] = useState(false);
	const paymentIdRef = useRef<string>('');
	const itemRef = useRef<TShopItem | undefined>(undefined);
	const promoCodeRef = useRef<string | undefined>(undefined);
	const isCustomer = useIsCustomer();

	const tryToBuy = useCallback(
		async (
			item: TShopItem,
			promoCodeInfo?: TShopPromoCodeResponse,
			meta?: Record<string, string | undefined>
		) => {
			if (!oneClickPaymentAvailable) {
				return openPaymentForm();
			}
			const applicableCode = promoCodeInfo?.Prices.find(
				({ Sku }) => Sku === item.sku
			);

			setPurchasing(true);

			try {
				const { sku, price, discountedPrice, currency } = item;
				const promoCode = !!applicableCode
					? promoCodeInfo?.PromoCode
					: undefined;

				const { paymentId } = await oneClickBuy({
					userId,
					sku,
					...meta,
					PromoCode: promoCode,
					country: country ?? 'N/A',
					shownInfo: {
						price: discountedPrice ?? price,
						currency,
					},
				}).unwrap();

				paymentIdRef.current = paymentId;
				itemRef.current = item;
				promoCodeRef.current = promoCode;
			} catch {
				setPurchasing(false);
				Toaster.showError({
					titleKey: 'requestFailedTitle',
					textKey: 'requestFailedMessage',
				});
			}
		},
		[country, oneClickBuy, oneClickPaymentAvailable, openPaymentForm, userId]
	);

	useEffect(
		() =>
			eventsInbox.onTypeMsg(EventType.OneClickPaymentCompleted, (payload) => {
				if (!paymentIdRef.current) {
					return;
				}

				const { paymentId, status, message } = payload;
				if (paymentId !== paymentIdRef.current) return;

				setPurchasing(false);
				paymentIdRef.current = '';

				if (status === 'completed') {
					if (itemRef.current) {
						GTMAnalytics.purchase({
							item: itemRef.current,
							transactionId: paymentId,
							isFirst: !isCustomer,
							coupon: promoCodeRef?.current,
						});
					}
					Toaster.showSuccess({ titleKey: 'paymentCompleted' });
					onPurchased();
				} else {
					Toaster.showError({
						titleKey: 'paymentNotCompleted',
						text: message,
					});
					openPaymentForm();
				}
				itemRef.current = undefined;
			}),
		[isCustomer, onPurchased, openPaymentForm]
	);

	return {
		oneClickPaymentAvailable,
		purchasing,
		tryToBuy,
		isPaymentFormOpened,
		openPaymentForm,
		closePaymentForm,
	};
};
